import { type TimelineEvent } from './timeline-utils';
import { Badge } from '@/components/ui/badge';
import { ImportanceBadge } from '@/components/ui/importance-badge';
import { ConfidenceBadge } from '@/components/ui/confidence-badge';
import { DatePopover } from '@/components/ui/date-popover';
import { SourceLinks } from '@/components/ui/source-links';
import { Users, BookOpen, Database } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import { AIDisclaimerBadge } from '@/components/ui/ai-disclaimer-badge';

interface TimelineEventModalProps {
    event: TimelineEvent | null;
    onClose: () => void;
}

/**
 * Modal showing full details of a timeline event
 */
export function TimelineEventModal({ event, onClose }: TimelineEventModalProps) {
    const isDarkMode = useDarkMode();

    if (!event) return null;

    return (
        <Dialog open={!!event} onOpenChange={(open: boolean) => !open && onClose()}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogTitle className="sr-only">Timeline Event: {event.title}</DialogTitle>
                <DialogDescription className="sr-only">
                    Details and historical context for the event: {event.title}
                </DialogDescription>
                <DialogHeader>
                    <div className="flex items-center justify-between pr-8">
                        <div className="text-xl font-semibold">
                            {event.title}
                        </div>
                    </div>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Event Image */}
                    {event.imageUrl && (
                        <div className="w-full">
                            <img
                                src={event.imageUrl}
                                alt={event.title}
                                className={`w-full h-48 object-cover rounded cursor-pointer hover:opacity-90 transition-opacity border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
                                onClick={() => window.open(event.imageUrl, '_blank')}
                            />
                        </div>
                    )}

                    {/* Date and metadata */}
                    <div className="space-y-3">
                        <div className="flex items-center gap-2 flex-wrap">
                            {/* Date with expandable details */}
                            {event.date && (
                                <DatePopover
                                    date={event.date}
                                    alternativeDates={event.alternativeDates}
                                    title="Event Date"
                                />
                            )}

                            {event.importance && (
                                <ImportanceBadge
                                    importance={event.importance}
                                    context="historical"
                                />
                            )}

                            {event.confidence && (
                                <ConfidenceBadge
                                    confidence={event.confidence}
                                    context="event"
                                />
                            )}

                            {event.verseRef && (
                                <Badge variant="secondary">
                                    <BookOpen className="h-3 w-3 mr-1" />
                                    Verse {event.verseRef}
                                </Badge>
                            )}

                            {/* Data Source Badge */}
                            <Badge variant="outline" className="text-xs">
                                <Database className="h-3 w-3 mr-1" />
                                {event.source === 'passage' && 'Passage Event'}
                                {event.source === 'book' && 'Book Event'}
                                {event.source === 'reference' && 'Reference Event'}
                            </Badge>

                            {/* Event Type Badge (for reference events) */}
                            {event.eventType && (
                                <Badge variant="outline" className="text-xs">
                                    {event.eventType === 'historical' && 'Historical'}
                                    {event.eventType === 'passage-event' && 'Passage Event'}
                                    {event.eventType === 'israel-ruler' && 'Israel Ruler'}
                                    {event.eventType === 'israel-prophet' && 'Israel Prophet'}
                                </Badge>
                            )}
                        </div>
                    </div>

                    {/* Summary */}
                    {event.summary && (
                        <p className="text-sm text-muted-foreground italic">
                            {event.summary}
                        </p>
                    )}

                    {/* Description */}
                    {event.description && (
                        <div className="space-y-1 bg-muted/30 p-3 rounded-md">
                            <p className="text-xs font-semibold text-foreground uppercase tracking-wide">Historical Context</p>
                            <p className="text-sm leading-relaxed text-foreground">
                                {event.description}
                            </p>
                        </div>
                    )}

                    {/* Relevance */}
                    {event.relevance && (
                        <div className={`space-y-1 p-3 rounded-md ${isDarkMode ? 'bg-blue-950/20' : 'bg-blue-50'}`}>
                            <p className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-blue-100' : 'text-blue-900'}`}>
                                {event.verseRef ? 'Passage Relevance' : 'Biblical Relevance'}
                            </p>
                            <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-blue-100' : 'text-blue-900'}`}>
                                {event.relevance}
                            </p>
                        </div>
                    )}

                    {/* Categories */}
                    {event.categories && event.categories.length > 0 && (
                        <div className="space-y-2">
                            <h4 className="text-sm font-semibold">Categories</h4>
                            <div className="flex items-center gap-2 flex-wrap">
                                {event.categories.map((cat, index) => (
                                    <Badge key={index} variant="secondary" className="text-xs">
                                        {cat.category}
                                        {cat.subcategory && `: ${cat.subcategory}`}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Key figures */}
                    {event.keyFigures && event.keyFigures.length > 0 && (
                        <div className="space-y-2">
                            <h4 className="text-sm font-semibold flex items-center gap-2">
                                <Users className="h-4 w-4" />
                                Key Figures
                            </h4>
                            <div className="flex items-center gap-2 flex-wrap">
                                {event.keyFigures.map((figure, index) => (
                                    <Badge key={index} variant="outline" className="text-xs">
                                        {figure}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Region and Empire */}
                    {(event.region || event.empire) && (
                        <div className="flex items-center gap-4 flex-wrap text-sm">
                            {event.region && (
                                <div>
                                    <span className="font-semibold">Region:</span> {event.region}
                                </div>
                            )}
                            {event.empire && (
                                <div>
                                    <span className="font-semibold">Empire:</span> {event.empire}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Impact */}
                    {event.impact && (
                        <div className="space-y-1">
                            <h4 className="text-sm font-semibold">Impact</h4>
                            <p className="text-sm text-muted-foreground">{event.impact}</p>
                        </div>
                    )}

                    {/* Archaeological Evidence */}
                    {event.archaeologicalEvidence && (
                        <div
                            className="space-y-1 p-3 rounded-md"
                            style={{
                                backgroundColor: isDarkMode ? '#1e293b' : '#f3f4f6',
                            }}
                        >
                            <p
                                className="text-xs font-semibold uppercase tracking-wide"
                                style={{ color: isDarkMode ? '#f1f5f9' : '#111827' }}
                            >
                                Archaeological Evidence
                            </p>
                            <p
                                className="text-sm leading-relaxed"
                                style={{ color: isDarkMode ? '#e2e8f0' : '#1f2937' }}
                            >
                                {event.archaeologicalEvidence}
                            </p>
                        </div>
                    )}

                    {/* Historicity */}
                    {event.historicity && (
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold">Historicity:</span>
                            <Badge
                                variant={event.historicity === 'well-attested' ? 'default' : 'outline'}
                                className={
                                    event.historicity === 'well-attested' ? 'bg-green-500' :
                                        event.historicity === 'attested' ? 'border-green-400 text-green-600' :
                                            'border-yellow-400 text-yellow-600'
                                }
                            >
                                {event.historicity}
                            </Badge>
                        </div>
                    )}



                    {/* Biblical References */}
                    {event.biblicalReferences?.length > 0 && (
                        <div className="space-y-2 pt-2 border-t border-border">
                            <h4 className="text-sm font-semibold">Biblical References</h4>
                            <div className="space-y-2">
                                {event.biblicalReferences.map((ref, idx) => (
                                    <div key={idx} className="space-y-1">
                                        <div className="flex items-center gap-2">
                                            <BookOpen className="h-3 w-3 text-muted-foreground" />
                                            <span className="text-sm font-semibold">{ref.book}</span>
                                            {ref.passages && ref.passages.length > 0 && (
                                                <span className="text-xs text-muted-foreground">
                                                    ({ref.passages.join(', ')})
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm text-muted-foreground ml-5">{ref.relevance}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Sources */}
                    {event.sources?.length > 0 && (
                        <div className="space-y-2 pt-2 border-t border-border">
                            <h4 className="text-sm font-semibold">Sources</h4>
                            <SourceLinks sources={event.sources} />
                        </div>
                    )}

                    {/* AI Disclaimer at the very end */}
                    <div className="pt-6 mt-8 flex justify-center border-t border-border/10">
                        <AIDisclaimerBadge
                            model={event.generatedBy}
                            contentId={event.title}
                            contextType="timeline-event"
                        />
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

